package ai.flowstorm.studio.ui

import ai.flowstorm.common.Toast
import ai.flowstorm.common.id.Id
import ai.flowstorm.common.id.StringId
import ai.flowstorm.common.state.Stateful
import ai.flowstorm.common.state.StatefulValue
import ai.flowstorm.common.ui.CardPanel
import ai.flowstorm.common.ui.Form
import ai.flowstorm.common.ui.UiHelpers
import ai.flowstorm.common.ui.encodeURIComponent
import ai.flowstorm.studio.model.Session
import ai.flowstorm.studio.model.Turn
import ai.flowstorm.studio.model.Utterance
import ai.flowstorm.studio.resources.ConversationalPlayerResource
import ai.flowstorm.studio.resources.space.SessionResource
import ai.flowstorm.studio.ui.data.UtteranceTable
import ai.flowstorm.studio.ui.data.transformToPureText
import ai.flowstorm.studio.ui.design.dialogue.panel.LogPanel
import ai.flowstorm.studio.ui.form.VoiceId
import io.kvision.core.Clear
import io.kvision.core.PosFloat
import io.kvision.core.Resize
import io.kvision.core.onEvent
import io.kvision.form.text.Text
import io.kvision.form.text.TextArea
import io.kvision.html.Div
import io.kvision.html.Link
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.i18n.tr
import io.kvision.panel.simplePanel
import io.kvision.panel.tabPanel
import io.kvision.state.bind
import io.kvision.state.observableListOf
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import io.kvision.utils.px
import kotlinx.browser.document
import org.w3c.dom.HTMLAudioElement

class ConversationPlayerPanel: CardPanel("Conversation Player"),
    Stateful<ConversationPlayerPanel.State> by StatefulValue(State()) {

    @Serializable
    data class FormData(
        val sessionId: String = "",
        val firstVoiceId: Id? = null,
        val secondVoiceId: Id? = null
    )

    data class State(
        val loading: Boolean = false,
        val data: Session? = null,
        val formData: FormData = FormData(""),
    )

    val mainScope = MainScope()

    private val textDownloadLink = Link(tr("Download text"), "").apply { padding = UiHelpers.spacing }
    private val audioDownloadLink = Link(tr("Download audio"), "").apply { padding = UiHelpers.spacing }

    private var audioUrl = ""

    private val firstVoice = VoiceId(tr("Voice 1")).apply {
        load(null)
    }

    private val secondVoice = VoiceId(tr("Voice 2")).apply {
        load(null)
    }

    private lateinit var previewDiv: Div

    private var previewUrl: TextArea =
        TextArea(rows = 1) {
            disabled = true
            input.resize = Resize.NONE
        }

    val form = Form(FormData.serializer()).apply {
        add(FormData::sessionId, Text(label = tr("Session ID"), value = ""), required = true)
        add(FormData::firstVoiceId, firstVoice)
        add(FormData::secondVoiceId, secondVoice)

        simplePanel {
            float = PosFloat.RIGHT
            button(tr("Pause")).bind(this@ConversationPlayerPanel) {
                marginRight = 10.px
            }.onClick {
                if (text == "Pause") {
                    (document.getElementById("player") as? HTMLAudioElement)?.pause()
                    text = "Play"
                } else {
                    (document.getElementById("player") as? HTMLAudioElement)?.play()
                    text = "Pause"
                }
            }
            button(tr("Swap")).bind(this@ConversationPlayerPanel) {
                marginRight = 10.px
            }.onClick {
                val tmp = secondVoice.value
                secondVoice.setValue(firstVoice.value)
                firstVoice.setValue(tmp)
            }
            button(tr("Generate Recording")).bind(this@ConversationPlayerPanel) {
                disabled = it.loading
                text = if (it.loading) tr("Generating") else tr("Generate Recording")
                marginRight = 10.px
            }.onClick {
                generate(
                    form.fields["sessionId"]?.getValue() as? String,
                    form.fields["firstVoiceId"]?.getValue() as? Id,
                    form.fields["secondVoiceId"]?.getValue() as? Id)

            }
            button(tr("Search")).bind(this@ConversationPlayerPanel) {
                disabled = it.loading
                text = if (it.loading) tr("Searching") else tr("Search")
            }.onClick {
                search(form.fields["sessionId"]?.getValue() as? String)
            }
        }
        simplePanel {
            clear = Clear.BOTH
        }
        onEvent {
            submit = {
                it.preventDefault()
            }
        }
    }

    init {
        addPanelHeader()
        simplePanel(className = "content-panel") {
            add(form)
            add(previewUrl)
            previewDiv = div()

            simplePanel().bind(this@ConversationPlayerPanel) { s: State ->
                if (!s.loading && s.data != null) {
                    val turns = observableListOf<Turn>().apply { addAll(s.data.turns) }
                    val utterances = observableListOf<Utterance>().apply { addAll(s.data.utterances) }
                    add(textDownloadLink.apply {
                        url = "data:text/plain;charset=utf-8," + encodeURIComponent(s.data.turnsToText())
                        setAttribute("download", s.data.sessionId + ".txt")
                    })
                    if (!previewUrl.value.isNullOrBlank()) {
                        add(audioDownloadLink.apply {
                            url = audioUrl
                            setAttribute("download", s.data.sessionId + ".mp3")
                        })
                    }
                    tabPanel {
                        addTab("Text", LogPanel().apply {
                            paddingLeft = UiHelpers.spacing
                            transformToPureText(turns)
                        })
                        addTab("Utterances", UtteranceTable(utterances, turns))
                    }
                }
            }
        }

    }

    fun search(sessionId: String?) = mainScope.launch {
        newState { copy(loading = true, data = null) }

        try {
            val res = SessionResource.last.load(StringId(sessionId!!))
            newState { copy(loading = false, data = res) }
        } catch (t: Throwable) {
            Toast.error("Search failed.")
            newState { copy(loading = false, data = null) }
        }
    }

    fun generate(sessionId: String?, firstVoiceId: Id?, secondVoiceId: Id?) = mainScope.launch {
        newState { copy(loading = true) }
        if (state.data == null) {
            search(sessionId)
        }
        try {
            if (state.data != null) {
                if (firstVoiceId == null || secondVoiceId == null) {
                    Toast.error("Select both voices")
                } else {
                    val request = ConversationalPlayerResource.Request(
                        state.data!!,
                        firstVoiceId,
                        secondVoiceId,
                    )
                    val ttsResponse = ConversationalPlayerResource.synthesize(request)
                    previewDiv.removeAll()
                    previewUrl.value = ttsResponse.url
                    audioUrl = ttsResponse.url
                    previewDiv.add(
                        Div(
                            content = """<audio autoplay id="player" style="width:100%;">
                        <source src="${ttsResponse.url}" type="${ttsResponse.type}" preload="none">
                        Your browser does not support the audio element.
                    </audio>""", rich = true
                        )
                    )
                }
            }
        } finally {
            newState { copy(loading = false) }
        }
    }
}